/**
 * Single source of truth for role-based access. Pure data + helpers, no deps,
 * importable by both the gateway (Node) and the frontend (Vite bundles the file).
 *
 * Levels per module: 'none' < 'read' < 'write'. A user can hold several roles;
 * effective access is the union (highest level per module wins).
 */
/* 'self' ist kein Fachbereich, sondern der Blick auf die EIGENEN Daten:
   mein Ferienstand, meine Stunden, mein Antrag. Jede Rolle hat ihn, auch die
   Buchhaltung - wer im Haus arbeitet, hat Ferien.

   Bewusst ein eigenes Modul und keine Ausnahme in 'hr': sonst müsste jeder,
   der seinen eigenen Ferienstand sehen will, Leserecht auf die Personalakten
   und Löhne aller Kollegen bekommen. */
export const MODULES = ['finance', 'hr', 'inventory', 'sales', 'projects', 'production', 'admin', 'self'];

export const LEVELS = { none: 0, read: 1, write: 2 };

export const ROLES = [
  'admin',
  'accountant',
  'hr',
  'purchaser',
  'sales',
  'project_manager',
  'executive',
  /* Die Rolle für alle, die im Haus arbeiten, aber kein Fachmodul brauchen:
     Werkstatt, Lager, Aussendienst. Sie sehen ausschliesslich ihre eigenen
     Daten. */
  'employee',
];

/** i18n key per role (frontend resolves the label). */
export const ROLE_LABELS = {
  admin: 'roles.admin',
  accountant: 'roles.accountant',
  hr: 'roles.hr',
  purchaser: 'roles.purchaser',
  sales: 'roles.sales',
  project_manager: 'roles.project_manager',
  executive: 'roles.executive',
  employee: 'roles.employee',
};

const W = 'write';
const R = 'read';
const N = 'none';

/* self steht überall auf W: die eigenen Ferien beantragt jeder selbst. */
export const MATRIX = {
  admin: { finance: W, hr: W, inventory: W, sales: W, projects: W, production: W, admin: W, self: W },
  accountant: { finance: W, hr: N, inventory: R, sales: R, projects: R, production: R, admin: N, self: W },
  hr: { finance: N, hr: W, inventory: N, sales: N, projects: N, production: N, admin: N, self: W },
  purchaser: { finance: R, hr: N, inventory: W, sales: R, projects: N, production: R, admin: N, self: W },
  sales: { finance: R, hr: N, inventory: R, sales: W, projects: N, production: N, admin: N, self: W },
  project_manager: { finance: N, hr: N, inventory: N, sales: R, projects: W, production: N, admin: N, self: W },
  executive: { finance: R, hr: R, inventory: R, sales: R, projects: R, production: R, admin: N, self: W },
  /* Nur die eigenen Daten. Diese Zeile ist der ganze Sinn der Rolle - wer sie
     erweitert, hebt sie auf. */
  employee: { finance: N, hr: N, inventory: N, sales: N, projects: N, production: N, admin: N, self: W },
};

/**
 * Welches Modul entscheidet über diese Anfrage?
 *
 * Normalerweise das Fachmodul des Pfadabschnitts. ABER: alles unter
 * `/<bereich>/me/...` betrifft ausschliesslich die eigenen Daten des
 * Anfragenden und fällt deshalb unter 'self'.
 *
 * Ohne diese Unterscheidung bräuchte jemand, der seinen eigenen Ferienstand
 * abrufen will, Leserecht auf 'hr' - und damit Einblick in die Löhne und
 * Krankheitstage aller Kollegen.
 *
 * @param {string} scope   erster Pfadabschnitt nach /api, z.B. 'hr'
 * @param {string} subPath Rest des Pfads, z.B. '/me/leave-requests'
 */
export function moduleForRequest(scope, subPath = '') {
  const erster = String(subPath).replace(/^\/+/, '').split(/[/?]/)[0];
  if (erster === 'me') {
    return 'self';
  }
  return SCOPE_TO_MODULE[scope];
}

/** Gateway scope segment -> module key. */
export const SCOPE_TO_MODULE = {
  finance: 'finance',
  hr: 'hr',
  inventory: 'inventory',
  sales: 'sales',
  projects: 'projects',
  production: 'production',
  automation: 'admin',
};

/**
 * @param {string[]} roles
 * @returns {Record<string, 'none'|'read'|'write'>}
 */
export function resolvePermissions(roles = []) {
  const result = {};
  for (const m of MODULES) result[m] = 'none';
  for (const role of roles) {
    const row = MATRIX[role];
    if (!row) continue;
    for (const m of MODULES) {
      if (LEVELS[row[m]] > LEVELS[result[m]]) result[m] = row[m];
    }
  }
  return result;
}

/** @returns {'read'|'write'} */
export function methodToAction(method) {
  const m = String(method || '').toUpperCase();
  return m === 'GET' || m === 'HEAD' || m === 'OPTIONS' ? 'read' : 'write';
}

/**
 * @param {string[]} roles
 * @param {string} module
 * @param {'read'|'write'} action
 */
export function can(roles, module, action) {
  const level = resolvePermissions(roles)[module] || 'none';
  return action === 'read' ? LEVELS[level] >= LEVELS.read : LEVELS[level] >= LEVELS.write;
}

export function isValidRole(role) {
  return ROLES.includes(role);
}
